import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Eye, FileText, FileCode, Paperclip, Send, Ban, Trash2 } from 'lucide-react';
import ModalContext from '../../../../../contexts/ModalContext';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import { STATUS_COLORS, mapStatus } from '../../invoiceStatus';
import { FRETE_LABELS, rotuloNota } from '../constants';
import { useEmissionPolling } from '../useEmissionPolling';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
  RowAction,
} from '../../../../../components/StandardPage';
import { OverflowCell } from '../../../../../components/OverflowCell';
import { openSignedAttachment } from '../../../../../utils/openSignedAttachment';
import { downloadRemoteFile } from '../../../../../utils/downloadRemoteFile';

const invoiceScreenKey = 'tax_invoice';
const PAGE_SIZE = 50;

const Badge = styled.span<{ type: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background-color: ${props => STATUS_COLORS[props.type]?.bg || '#f2f2f2'};
  color: ${props => STATUS_COLORS[props.type]?.text || '#666'};
  white-space: nowrap;
`;

const Dot = styled.span<{ type: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${props => STATUS_COLORS[props.type]?.dot || '#999'};
`;



const ErrorMessage = styled.div`
  text-align: center;
  color: #9B111E;
  padding: 20px;
  width: 100%;
`;

const formatInvoiceNumber = (row: any) =>
  row.serie && row.numeroNF
    ? `${row.serie}.${String(row.numeroNF).padStart(6, '0')}`
    : '—';

const getInvoiceTotal = (row: any) => {
  const prods = row.InvoiceProduct?.reduce((s: number, p: any) => s + Number(p.valorTotal || 0), 0) || 0;
  const servs = row.InvoiceService?.reduce((s: number, p: any) => s + Number(p.valorTotal || 0), 0) || 0;
  return Number(row.valorTotalNota || (prods + servs));
};

const formatCnpjCpf = (cnpj?: string | null, cpf?: string | null) => {
  const doc = cnpj || cpf || '';
  const clean = doc.replace(/\D/g, '');
  if (clean.length === 14) {
    return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  if (clean.length === 11) {
    return clean.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return doc || '—';
};

const formatContribuinte = (row: any) => {
  if (row.CompanyCliente?.indicatorIe) return row.CompanyCliente.indicatorIe;
  if (row.CompanyCliente?.ie) return 'Contribuinte';
  return 'Não Contribuinte';
};

const formatAddress = (row: any) => {
  const addr = row.CompanyCliente?.Address || row.EnderecoEntrega;
  if (!addr) return '—';
  const parts: string[] = [];
  if (addr.street) parts.push(addr.street);
  if (addr.number) parts.push(`Nº ${addr.number}`);
  if (addr.complement) parts.push(addr.complement);
  if (addr.Neighborhood?.name) parts.push(addr.Neighborhood.name);
  if (addr.Neighborhood?.City?.name) {
    const city = addr.Neighborhood.City.name;
    const uf = addr.Neighborhood.City.State?.uf ? ` - ${addr.Neighborhood.City.State.uf}` : '';
    parts.push(`${city}${uf}`);
  }
  if (addr.zipCode) parts.push(`CEP: ${addr.zipCode}`);
  return parts.length > 0 ? parts.join(', ') : '—';
};

const formatItemsSummary = (row: any) => {
  const prods = row.InvoiceProduct || [];
  const servs = row.InvoiceService || [];
  const totalItems = prods.length + servs.length;
  if (totalItems === 0) return '—';
  const names = [
    ...prods.map((p: any) => p.descricaoProduto || p.codigoProduto || 'Produto'),
    ...servs.map((s: any) => s.descricaoServico || 'Serviço'),
  ];
  return names.join(', ');
};

const formatItemsTooltip = (row: any) => {
  const prods = row.InvoiceProduct || [];
  const servs = row.InvoiceService || [];
  const all: string[] = [];
  prods.forEach((p: any, i: number) => {
    const qtd = p.quantidade ? `Qtd: ${Number(p.quantidade)}` : '';
    const val = p.valorTotal ? `R$ ${Number(p.valorTotal).toFixed(2)}` : '';
    all.push(`${i + 1}. [Prod] ${p.descricaoProduto || p.codigoProduto || 'Item'}${qtd ? ` | ${qtd}` : ''}${val ? ` | ${val}` : ''}`);
  });
  servs.forEach((s: any, i: number) => {
    const qtd = s.quantidade ? `Qtd: ${Number(s.quantidade)}` : '';
    const val = s.valorTotal ? `R$ ${Number(s.valorTotal).toFixed(2)}` : '';
    all.push(`${prods.length + i + 1}. [Serv] ${s.descricaoServico || 'Serviço'}${qtd ? ` | ${qtd}` : ''}${val ? ` | ${val}` : ''}`);
  });
  return all.length > 0 ? all.join('\n') : '—';
};

const formatPayment = (row: any) => {
  const payments = row.InvoicePayment || [];
  if (payments.length > 0) {
    return payments.map((p: any) => p.PaymentMethod?.name || p.descricao || 'Pagamento').join(', ');
  }
  if (row.condicaoPagamento) {
    const map: Record<string, string> = {
      A_VISTA: 'À Vista',
      A_PRAZO: 'A Prazo',
      OUTROS: 'Outros',
    };
    return map[row.condicaoPagamento] || row.condicaoPagamento;
  }
  return '—';
};

const formatFreightMode = (mode?: string | null) => {
  if (!mode) return '—';
  return FRETE_LABELS[mode] || mode;
};

const formatNatureza = (nat?: string | null) => {
  if (!nat) return '—';
  const map: Record<string, string> = {
    VENDA: 'Venda',
    DEVOLUCAO: 'Devolução',
    TRANSFERENCIA: 'Transferência',
    REMESSA: 'Remessa',
    RETORNO: 'Retorno',
    BONIFICACAO: 'Bonificação',
    EXPORTACAO: 'Exportação',
    IMPORTACAO: 'Importação',
    OUTRA: 'Outra',
  };
  return map[nat] || nat;
};

const formatTipoOperacao = (tipo?: string | null, invType?: string | null) => {
  if (tipo === 'ENTRADA') return 'Entrada';
  if (tipo === 'SAIDA') return 'Saída';
  return invType || '—';
};

const formatFinalidade = (fin?: string | null) => {
  if (!fin) return '—';
  const map: Record<string, string> = {
    NORMAL: 'Normal',
    COMPLEMENTAR: 'Complementar',
    AJUSTE: 'Ajuste',
    DEVOLUCAO: 'Devolução',
  };
  return map[fin] || fin;
};

const getInvoiceIcms = (row: any) => {
  const sum = row.InvoiceProduct?.reduce((s: number, p: any) => s + Number(p.valorICMS || 0), 0) || 0;
  return sum;
};

const getInvoiceIpi = (row: any) => {
  const sum = row.InvoiceProduct?.reduce((s: number, p: any) => s + Number(p.valorIPI || 0), 0) || 0;
  return sum;
};

const extractInvoicePage = (data: any): { list: any[]; totalValue: number } => {
  const list = Array.isArray(data) ? data : (data?.data || []);
  const totalValue = Array.isArray(data) ? data.length : (data?.total ?? list.length);
  return { list, totalValue };
};

const InvoiceTable: React.FC = () => {
  const { setModalActive } = useContext(ModalContext);
  const { setFeedback } = useContext(FeedbackContext);
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<any[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{ id: number; label: string } | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const loadedCountRef = useRef(0);

  const { refresh, search, appliedFilters } = useOutletContext<{ refresh?: boolean; search?: string; appliedFilters?: any[] }>() ?? {};

  const buildParams = useCallback((take: number, skip: number) => {
    const params: any = { take, skip };
    if (search && search.trim() !== '') {
      params.search = search;
    }
    if (appliedFilters && appliedFilters.length > 0) {
      params.filters = JSON.stringify(appliedFilters);
    }
    return params;
  }, [search, appliedFilters]);

  const loadInvoices = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const take = silent ? Math.max(PAGE_SIZE, loadedCountRef.current) : PAGE_SIZE;
      const response = await ApiService.fiscal.getInvoices(buildParams(take, 0));
      const { list, totalValue } = extractInvoicePage(response.data);
      loadedCountRef.current = list.length;
      setInvoices(list);
      setTotal(totalValue);
      if (!silent) setError(null);
    } catch {
      if (!silent) setError('Erro ao carregar as notas fiscais.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [buildParams]);

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const response = await ApiService.fiscal.getInvoices(buildParams(PAGE_SIZE, loadedCountRef.current));
      const { list, totalValue } = extractInvoicePage(response.data);
      setInvoices((prev) => {
        const merged = [...prev, ...list];
        loadedCountRef.current = merged.length;
        return merged;
      });
      setTotal(totalValue);
    } catch {
      setFeedback({ message: 'Erro ao carregar mais notas fiscais.', isOpen: true, statusOk: false });
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, setFeedback]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices, refresh]);

  const hasPending = useMemo(
    () => invoices.some((i: any) => i.status === 'EMITINDO'),
    [invoices],
  );

  const syncPending = useCallback(async () => {
    const pendentes = invoices.filter((i: any) => i.status === 'EMITINDO');
    await Promise.allSettled(
      pendentes.map((i: any) => ApiService.fiscal.nfeStatus(i.id)),
    );
    await loadInvoices(true);
  }, [invoices, loadInvoices]);
  useEmissionPolling({ isEmitindo: hasPending, onTick: syncPending });

  const openModal = useCallback((id?: number) => {
    setModalActive(false);
    navigate(id ? `/panel/invoice/edit/${id}` : '/panel/invoice/create');
  }, [navigate, setModalActive]);

  const openView = useCallback((id: number) => {
    navigate(`/panel/invoice/view/${id}`);
  }, [navigate]);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      await ApiService.fiscal.cancelar(cancelTarget.id);
      setFeedback({ message: 'Cancelamento solicitado.', isOpen: true, statusOk: true });
      setCancelTarget(null);
      loadInvoices(true);
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao cancelar a nota.', isOpen: true, statusOk: false });
    } finally {
      setCancelLoading(false);
    }
  }, [cancelTarget, setFeedback, loadInvoices]);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteTarget === null) return;
    setDeleteLoading(true);
    try {
      await ApiService.fiscal.deleteInvoice(deleteTarget);
      setFeedback({ message: 'Nota excluída.', isOpen: true, statusOk: true });
      setDeleteTarget(null);
      loadInvoices(true);
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao excluir a nota.', isOpen: true, statusOk: false });
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, setFeedback, loadInvoices]);

  const columns = useMemo<StandardColumn<any>[]>(() => [
    {
      id: 'numeroNF',
      label: 'NF',
      accessor: (row) => formatInvoiceNumber(row),
      cell: (row) => <strong>{formatInvoiceNumber(row)}</strong>,
    },
    {
      id: 'orderId',
      label: 'Pedido',
      accessor: (row) => row.orderId ?? '',
      cell: (row) => <span style={{ color: '#555', fontSize: '13px' }}>{row.orderId || '—'}</span>,
    },
    {
      id: 'chaveAcesso',
      label: 'Chave de acesso',
      accessor: (row) => row.chaveAcesso ?? '',
      cell: (row) => (
        <OverflowCell text={row.chaveAcesso || '—'} maxWidth={160} />
      ),
    },
    {
      id: 'destinatario',
      label: 'Destinatário',
      accessor: (row) => row.CompanyCliente?.fantasyName || row.CompanyCliente?.companyName || '—',
      cell: (row) => (
        <OverflowCell
          text={row.CompanyCliente?.fantasyName || row.CompanyCliente?.companyName || '—'}
          maxWidth={180}
        />
      ),
    },
    {
      id: 'cpfCnpj',
      label: 'CPF/CNPJ',
      accessor: (row) => formatCnpjCpf(row.CompanyCliente?.cnpj, row.CompanyCliente?.cpf),
      cell: (row) => (
        <OverflowCell text={formatCnpjCpf(row.CompanyCliente?.cnpj, row.CompanyCliente?.cpf)} maxWidth={160} />
      ),
    },
    {
      id: 'contribuinteIcms',
      label: 'Contribuinte ICMS',
      accessor: (row) => formatContribuinte(row),
      cell: (row) => (
        <OverflowCell text={formatContribuinte(row)} maxWidth={140} />
      ),
    },
    {
      id: 'inscricaoEstadual',
      label: 'Inscrição Estadual',
      accessor: (row) => row.CompanyCliente?.ie || '—',
      cell: (row) => (
        <OverflowCell text={row.CompanyCliente?.ie || '—'} maxWidth={140} />
      ),
    },
    {
      id: 'email',
      label: 'E-mail',
      accessor: (row) => row.CompanyCliente?.email || '—',
      cell: (row) => (
        <OverflowCell text={row.CompanyCliente?.email || '—'} maxWidth={180} />
      ),
    },
    {
      id: 'endereco',
      label: 'Endereço',
      accessor: (row) => formatAddress(row),
      cell: (row) => (
        <OverflowCell text={formatAddress(row)} maxWidth={220} />
      ),
    },
    {
      id: 'empresaEmissora',
      label: 'Empresa Emissora',
      accessor: (row) => row.CompanyEmissor?.companyName || row.CompanyEmissor?.fantasyName || '—',
      cell: (row) => (
        <OverflowCell
          text={row.CompanyEmissor?.companyName || row.CompanyEmissor?.fantasyName || '—'}
          maxWidth={180}
        />
      ),
    },
    {
      id: 'naturezaOperacao',
      label: 'Natureza da Operação',
      accessor: (row) => formatNatureza(row.naturezaOperacao),
      cell: (row) => (
        <OverflowCell text={formatNatureza(row.naturezaOperacao)} maxWidth={160} />
      ),
    },
    {
      id: 'tipoOperacao',
      label: 'Tipo de Operação',
      accessor: (row) => formatTipoOperacao(row.tipoOperacao, row.invoiceType),
      cell: (row) => (
        <OverflowCell text={formatTipoOperacao(row.tipoOperacao, row.invoiceType)} maxWidth={140} />
      ),
    },
    {
      id: 'finalidade',
      label: 'Finalidade',
      accessor: (row) => formatFinalidade(row.finalidade),
      cell: (row) => (
        <OverflowCell text={formatFinalidade(row.finalidade)} maxWidth={140} />
      ),
    },
    {
      id: 'cfop',
      label: 'CFOP',
      accessor: (row) => row.InvoiceProduct?.[0]?.cfop || row.InvoiceService?.[0]?.cfop || '—',
      cell: (row) => {
        const v = row.InvoiceProduct?.[0]?.cfop || row.InvoiceService?.[0]?.cfop || '—';
        return v !== '—' ? <strong style={{ color: '#0085CC' }}>{v}</strong> : <span>—</span>;
      },
    },
    {
      id: 'itensNota',
      label: 'Itens da Nota',
      accessor: (row) => formatItemsSummary(row),
      cell: (row) => {
        const summary = formatItemsSummary(row);
        if (summary === '—') return '—';
        return (
          <OverflowCell text={formatItemsTooltip(row)} forceTooltip maxWidth={220}>
            {summary}
          </OverflowCell>
        );
      },
    },
    {
      id: 'produtos',
      label: 'Produtos',
      accessor: (row) => {
        const prods = row.InvoiceProduct || [];
        return prods.map((p: any) => p.descricaoProduto || p.codigoProduto || 'Produto').join(', ') || '—';
      },
      cell: (row) => {
        const prods = row.InvoiceProduct || [];
        const summary = prods.map((p: any) => p.descricaoProduto || p.codigoProduto || 'Produto').join(', ') || '—';
        if (summary === '—') return '—';
        return (
          <OverflowCell text={summary} maxWidth={200}>
            {summary}
          </OverflowCell>
        );
      },
    },
    {
      id: 'pagamento',
      label: 'Pagamento',
      accessor: (row) => formatPayment(row),
      cell: (row) => (
        <OverflowCell text={formatPayment(row)} maxWidth={160} />
      ),
    },
    {
      id: 'modalidadeFrete',
      label: 'Modalidade',
      accessor: (row) => formatFreightMode(row.modalidadeFrete),
      cell: (row) => (
        <OverflowCell text={formatFreightMode(row.modalidadeFrete)} maxWidth={160} />
      ),
    },
    {
      id: 'transportadora',
      label: 'Transportadora',
      accessor: (row) => row.CompanyTransportadora?.companyName || row.CompanyTransportadora?.fantasyName || '—',
      cell: (row) => (
        <OverflowCell
          text={row.CompanyTransportadora?.companyName || row.CompanyTransportadora?.fantasyName || '—'}
          maxWidth={180}
        />
      ),
    },
    {
      id: 'dadosAdicionais',
      label: 'Dados Adicionais',
      accessor: (row) => row.dadosAdicionais || '—',
      cell: (row) => (
        <OverflowCell text={row.dadosAdicionais || '—'} maxWidth={200} />
      ),
    },
    {
      id: 'icms',
      label: 'ICMS',
      accessor: (row) => getInvoiceIcms(row),
      cell: (row) => {
        const v = getInvoiceIcms(row);
        return <span>{v > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : '—'}</span>;
      },
    },
    {
      id: 'ipi',
      label: 'IPI',
      accessor: (row) => getInvoiceIpi(row),
      cell: (row) => {
        const v = getInvoiceIpi(row);
        return <span>{v > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : '—'}</span>;
      },
    },
    {
      id: 'valorTotal',
      label: 'Valor total',
      accessor: (row) => getInvoiceTotal(row),
      cell: (row) => {
        const v = getInvoiceTotal(row);
        return <strong>{v > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : '—'}</strong>;
      },
      exportValue: (row) => {
        const v = getInvoiceTotal(row);
        return v > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : '—';
      },
    },
    {
      id: 'createdAt',
      label: 'Emissão',
      accessor: (row) => row.createdAt ?? '',
      cell: (row) => <>{row.createdAt ? new Date(row.createdAt).toLocaleDateString('pt-BR') : '—'}</>,
      exportValue: (row) => (row.createdAt ? new Date(row.createdAt).toLocaleDateString('pt-BR') : '—'),
    },
    {
      id: 'status',
      label: 'Status',
      accessor: (row) => mapStatus(row.status),
      cell: (row) => {
        const label = mapStatus(row.status);
        return (
          <Badge type={label}>
            <Dot type={label} />
            {label}
          </Badge>
        );
      },
    },
  ], []);

  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(invoiceScreenKey, columns);

  const buildPrimaryAction = (row: any): RowAction => {
    const { id, status, serie, numeroNF } = row;
    const label = rotuloNota(serie, numeroNF, id);

    if (status === 'RASCUNHO' || status === 'NEGADA') {
      return { key: 'emit', label: 'Emitir', icon: <Send size={16} />, onClick: () => openModal(id) };
    }
    if (status === 'EMITINDO') {
      return { key: 'emitting', label: 'Emitindo…', icon: <Send size={16} />, onClick: () => {}, disabled: true, title: 'Emissão em andamento' };
    }
    if (status === 'EMITIDA') {
      return { key: 'cancel', label: 'Cancelar NF-e', icon: <Ban size={16} />, onClick: () => setCancelTarget({ id, label }) };
    }
    return { key: 'no-action', label: 'Sem ação disponível', icon: <Ban size={16} />, onClick: () => {}, disabled: true };
  };



  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  return (
    <>
      <StandardDataTable
        columns={columns}
        data={invoices}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        countLabel='notas fiscais'
        totalCount={total}
        hasMore={total !== null && invoices.length < total}
        isLoading={loading || loadingMore}
        onLoadMore={handleLoadMore}
        exportFileName='notas-fiscais'
        emptyMessage='Nenhuma nota fiscal encontrada.'
        rowActions={(row) => [
          buildPrimaryAction(row),
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => openView(row.id) },
          {
            label: 'Abrir DANFE',
            icon: <FileText size={16} />,
            onClick: () => {
              if (row.linkDanfe) {
                void openSignedAttachment(row.linkDanfe);
              }
            },
            disabled: !row.linkDanfe,
            title: !row.linkDanfe ? 'DANFE indisponível' : undefined,
          },
          {
            label: 'Baixar XML',
            icon: <FileCode size={16} />,
            onClick: () => {
              if (row.linkXml) {
                void downloadRemoteFile(row.linkXml, `nota-${row.number || row.id}.xml`);
              }
            },
            disabled: !row.linkXml,
            title: !row.linkXml ? 'XML indisponível' : undefined,
          },
          {
            label: 'Anexos',
            icon: <Paperclip size={16} />,
            onClick: () => {},
            disabled: true,
            title: 'Anexos (em breve)',
          },
          // Mesma janela do backend (invoice.service deleteInvoice): só Disponível e Negada.
          ...(row.status === 'RASCUNHO' || row.status === 'NEGADA'
            ? [{ label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => setDeleteTarget(row.id), danger: true }]
            : []),
        ]}
      />

      <ConfirmationModal
        isOpen={!!cancelTarget}
        title="Cancelar NF-e"
        message={
          cancelTarget
            ? `Tem certeza que deseja cancelar a nota ${cancelTarget.label}? O cancelamento é irreversível e só é permitido em até 24h da autorização.`
            : ''
        }
        actions={[
          { label: 'Voltar', onClick: () => setCancelTarget(null), variant: 'outline' },
          {
            label: cancelLoading ? 'Cancelando…' : 'Cancelar NF-e',
            onClick: handleConfirmCancel,
            variant: 'danger',
          },
        ]}
      />

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        title="Excluir nota"
        message="Tem certeza que deseja excluir esta nota disponível? Esta ação não pode ser desfeita."
        actions={[
          { label: 'Voltar', onClick: () => setDeleteTarget(null), variant: 'outline' },
          {
            label: deleteLoading ? 'Excluindo…' : 'Excluir nota',
            onClick: handleConfirmDelete,
            variant: 'danger',
          },
        ]}
      />
    </>
  );
};

export default InvoiceTable;
